import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  DollarSign,
  TrendingUp,
  FileText,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectExpenseService from '../../../services/projectExpenseService'
import RevisionHistoryModal from '../../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const ProjectExpenseList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalExpenses: 0,
    totalAmount: 0,
    draft: 0,
    submitted: 0,
    approved: 0,
    rejected: 0,
    paid: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedExpenseData, setSelectedExpenseData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [expensesResponse, statsResponse] = await Promise.all([
        projectExpenseService.getAllProjectExpenses(),
        projectExpenseService.getProjectExpenseStats()
      ])

      if (expensesResponse.success !== false && expensesResponse.expenses) {
        setData(expensesResponse.expenses || [])
        
        // Show toast if no data
        if ((expensesResponse.expenses || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project expenses found. Create your first expense to get started!')
        }
      } else {
        toast.error(expensesResponse.message || 'Failed to load project expenses')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load project expenses data')
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/expenses/new')
  }

  const handleEdit = (expense) => {
    const expenseId = expense._id || expense.id
    if (!expenseId) {
      toast.error('Expense ID not found')
      return
    }
    navigate(`/projects/expenses/edit/${expenseId}`)
  }

  const handleView = (expense) => {
    const expenseId = expense._id || expense.id
    if (!expenseId) {
      toast.error('Expense ID not found')
      return
    }
    navigate(`/projects/expenses/view/${expenseId}`)
  }

  const handleDelete = (expense) => {
    setDeleteItem(expense)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const expenseId = deleteItem._id || deleteItem.id
      if (!expenseId) {
        toast.error('Expense ID not found')
        return
      }

      const response = await projectExpenseService.deleteProjectExpense(expenseId)
      if (response.success) {
        toast.success('Project expense deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete project expense')
      }
    } catch (error) {
      console.error('Error deleting project expense:', error)
      toast.error('Failed to delete project expense')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (expense) => {
    setSelectedExpenseData(expense)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'expenseNumber',
      label: 'Expense #',
      sortable: true,
      render: (value, row) => (
        <div className="font-medium text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        
        return <div className="font-medium text-gray-900">{projectName}</div>
      }
    },
    {
      key: 'employee',
      label: 'Employee',
      sortable: true,
      render: (value, row) => {
        const employee = row.employee
        if (!employee) return <span className="text-gray-500">N/A</span>
        
        const employeeName = typeof employee === 'string' 
          ? employee 
          : (employee.employeeName || employee.name || 'N/A')
        
        return <div className="font-medium text-gray-900">{employeeName}</div>
      }
    },
    {
      key: 'expenseCategory',
      label: 'Category',
      sortable: true,
      render: (value, row) => {
        const category = row.expenseCategory
        if (!category) return <span className="text-gray-500">N/A</span>
        
        const categoryName = typeof category === 'string' 
          ? category 
          : (category.categoryName || category.name || 'N/A')
        
        return <div className="font-medium text-gray-900">{categoryName}</div>
      }
    },
    {
      key: 'expenseDate',
      label: 'Date',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        
        const formatDate = (dateString) => {
          const date = new Date(dateString)
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
          const day = String(date.getDate()).padStart(2, '0')
          const month = months[date.getMonth()]
          const year = date.getFullYear()
          return `${day}-${month}-${year}`
        }
        
        return <div className="font-medium text-gray-900">{formatDate(value)}</div>
      }
    },
    {
      key: 'totalAmount',
      label: 'Amount',
      sortable: true,
      render: (value, row) => {
        if (!value) return <span className="text-gray-500">N/A</span>
        const currency = row.currency || 'INR'
        return <div className="font-medium text-gray-900">{`${currency} ${value.toLocaleString()}`}</div>
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, row) => {
        const status = row.status
        if (!status) return <span className="text-gray-500">N/A</span>
        
        const statusColors = {
          'Draft': '#6B7280',
          'Submitted': '#3B82F6',
          'Approved': '#10B981',
          'Rejected': '#EF4444',
          'Paid': '#059669'
        }
        
        const statusColor = statusColors[status] || '#6B7280'
        
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {status}
          </span>
        )
      }
    }
  ]

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Transactions',
      path: '/projects?tab=transactions',
      onClick: () => navigate('/projects?tab=transactions')
    },
    {
      label: 'Project Expenses',
      path: '/projects/expenses/list',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Expenses</h1>
          <p>Track and manage all project-related expenses</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Expense
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalExpenses || 0}</div>
          <div className="metric-title">Total Expenses</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalAmount ? `₹${(stats.totalAmount / 1000).toFixed(1)}K` : '₹0'}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All expenses</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.approved || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Ready for payment</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.submitted || 0}</div>
          <div className="metric-title">Pending Approval</div>
          <div className="metric-subtitle">Awaiting review</div>
        </div>
      </div>

      {/* Expense List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search expenses..."
          emptyMessage="No project expenses found. Create your first expense to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Expense"
        message={`Are you sure you want to delete expense "${deleteItem?.expenseNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedExpenseData}
        title="Project Expense Revision History"
      />
    </div>
  )
}

export default ProjectExpenseList

